import React, {useEffect, useRef, useState} from "react";
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import ApiService from "../../../services/ApiService";
import {THANKS_STORAGE_PREFIX} from "./DonationPanel";
import styles from "./v2.module.css";

const MAX_AGE_MS = 6 * 60 * 60 * 1000
const STATUS_TRIES = 3
const STATUS_DELAY_MS = 2000

// Le paiement créé par l'écran de don : c'est son état, et non l'adresse de retour
// (servie aussi en cas d'annulation), qui ouvre le remerciement.
export function rememberPaiement(campagneId, paiementId) {
    if (!campagneId || !paiementId) return
    try {
        const key = THANKS_STORAGE_PREFIX + campagneId
        const data = JSON.parse(window.sessionStorage.getItem(key) || "null")
        if (data) window.sessionStorage.setItem(key, JSON.stringify({...data, paiementId}))
    } catch (e) { /* stockage indisponible : pas d'écran de remerciement */ }
}

async function isCompleted(paiementId) {
    for (let i = 0; i < STATUS_TRIES; i++) {
        try {
            const res = await ApiService.get("/paiements/check/status/" + paiementId)
            if (res?.status === "COMPLETED") return true
        } catch (e) { /* on réessaie */ }
        if (i < STATUS_TRIES - 1) await new Promise(resolve => setTimeout(resolve, STATUS_DELAY_MS))
    }
    return false
}

function readDonation(campagneId) {
    try {
        const raw = window.sessionStorage.getItem(THANKS_STORAGE_PREFIX + campagneId)
        if (!raw) return null
        const data = JSON.parse(raw)
        if (!data?.at || Date.now() - data.at > MAX_AGE_MS) return null
        return data
    } catch (e) {
        return null
    }
}

// Lien à partager : la page, sans le lien personnel d'un ambassadeur (amb) ni le code (pc).
function shareableUrl() {
    try {
        const url = new URL(window.location.href)
        url.searchParams.delete("merci")
        url.searchParams.delete("amb")
        url.searchParams.delete("pc")
        return url.toString()
    } catch (e) {
        return window.location.origin + window.location.pathname
    }
}

/**
 * Écran de remerciement affiché au retour sur la page, une fois le paiement créé par
 * l'écran de don confirmé (COMPLETED). Le montant vient du navigateur du donateur
 * (sessionStorage), jamais de l'URL.
 */
export default function ThankYou({context, campagneData, title, progressSlot, formatMoney}) {
    const [open, setOpen] = useState(false)
    const [donation, setDonation] = useState(null)
    const [shareUrl, setShareUrl] = useState("")
    const [copied, setCopied] = useState(false)
    const closeRef = useRef(null)
    const panelRef = useRef(null)
    const previousFocus = useRef(null)

    useEffect(() => {
        const data = readDonation(campagneData.id)
        if (!data?.paiementId) return
        let cancelled = false
        isCompleted(data.paiementId).then(completed => {
            if (cancelled || !completed) return
            try {
                window.sessionStorage.removeItem(THANKS_STORAGE_PREFIX + campagneData.id)
            } catch (e) { /* rien à nettoyer */ }
            setDonation(data)
            setShareUrl(shareableUrl())
            previousFocus.current = document.activeElement
            setOpen(true)
        })
        return () => { cancelled = true }
    }, [])

    useEffect(() => {
        if (!open) return
        closeRef.current?.focus()
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault()
                close()
            }
            if (e.key !== "Tab") return
            const panel = panelRef.current
            if (!panel) return
            const controls = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
            if (!controls.length) {
                e.preventDefault()
                panel.focus()
                return
            }
            const first = controls[0]
            const last = controls[controls.length - 1]
            if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
                e.preventDefault()
                first.focus()
            }
        }
        document.addEventListener("keydown", onKey)
        return () => document.removeEventListener("keydown", onKey)
    }, [open])

    const close = () => {
        setOpen(false)
        try {
            window.sessionStorage.removeItem(THANKS_STORAGE_PREFIX + campagneData.id)
        } catch (e) { /* rien à nettoyer */ }
        previousFocus.current?.focus?.()
    }

    if (!open) return null

    const message = t`J'ai soutenu « ${title} ». Vous aussi, participez :`
    const whatsappUrl = "https://wa.me/?text=" + encodeURIComponent(message + " " + shareUrl)
    const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function"

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl)
            setCopied(true)
        } catch (e) {
            setCopied(false)
        }
    }

    const amountText = donation?.amount
        ? (donation.split > 1
            ? t`${formatMoney(Math.ceil(donation.amount / donation.split), donation.devise)} par mois pendant ${donation.split} mois`
            : donation.monthly ? t`${formatMoney(donation.amount, donation.devise)} par mois` : formatMoney(donation.amount, donation.devise))
        : null

    return (
        <div className={styles.thanks} onClick={(e) => { if (e.target === e.currentTarget) close() }}>
            <div ref={panelRef} className={styles.thanksPanel} role="dialog" aria-modal="true" aria-labelledby="cv-thanks-title" tabIndex={-1}>
                <h2 id="cv-thanks-title" className={styles.thanksTitle}><Trans>Merci pour votre don !</Trans></h2>
                <p className={styles.thanksText}>
                    {amountText
                        ? <Trans>Votre don de <strong>{amountText}</strong> fait avancer « {title} ».</Trans>
                        : <Trans>Votre générosité fait avancer « {title} ».</Trans>}
                    {" "}
                    <Trans>Votre reçu vous est envoyé par email.</Trans>
                </p>

                {progressSlot && <div style={{marginTop: 18}}>{progressSlot}</div>}

                <p className={styles.thanksText} style={{marginTop: 18}}>
                    <Trans>Chaque partage peut amener un nouveau donateur.</Trans>
                </p>
                <div className={styles.shareRow}>
                    <a className={`${styles.shareButton} ${styles.shareWhatsapp}`} href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                        <Trans>Partager sur WhatsApp</Trans>
                    </a>
                    {canShare ? (
                        <button type="button" className={styles.shareButton} onClick={() => navigator.share({title, text: message, url: shareUrl}).catch(() => {})}>
                            <Trans>Partager autrement</Trans>
                        </button>
                    ) : (
                        <button type="button" className={styles.shareButton} onClick={copy}>
                            {copied ? <Trans>Lien copié</Trans> : <Trans>Copier le lien</Trans>}
                        </button>
                    )}
                </div>

                <button ref={closeRef} type="button" className={styles.secondary} style={{width: "100%"}} onClick={close}>
                    <Trans>Revenir à la campagne</Trans>
                </button>
            </div>
        </div>
    )
}
